import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';

import type {
  Appearance,
  CarouselBanner,
  CommunicationBanner,
  EmbeddedMenuRow,
  FiltersAndLabels,
  MenuRow,
  MenuTab,
} from '@jane/business-admin/types';
import { tMenuRow } from '@jane/business-admin/types';
import { fetchWithDecode } from '@jane/business-admin/util';
import { janeApiV2 } from '@jane/shared/data-access';

import { STORES_URL } from './stores';

interface MenuRowCreateParams {
  applicability_rules?: { filter_type: string; filter_value: string }[];
  custom_display_name?: string;
  enabled: boolean;
  menu_products?: number[];
  menu_row_type: string;
  ranking?: number;
  row_type: string;
  schedule?: any;
}
const createMenuRow = async (
  storeId: string,
  data: MenuRowCreateParams
): Promise<null> => {
  return await janeApiV2.post<null>(
    `${STORES_URL}/${storeId}/menu/menu_row`,
    data
  );
};
export const useCreateMenuRow = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MenuRowCreateParams) => createMenuRow(storeId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'active',
      });
      queryClient.resetQueries({
        queryKey: ['menu-paginated', storeId],
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'active',
      });
    },
  });
};

const updateMenuRow = async (
  storeId: string,
  data: MenuRowCreateParams,
  rowId: number
): Promise<null> => {
  return await janeApiV2.patch<null>(
    `${STORES_URL}/${storeId}/menu/menu_row/${rowId}`,
    data
  );
};
export const useUpdateMenuRow = (storeId: string, rowId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: MenuRowCreateParams) =>
      updateMenuRow(storeId, data, rowId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

const deleteMenuRow = async (storeId: string, rowId: number): Promise<null> => {
  return await janeApiV2.delete<null>(
    `${STORES_URL}/${storeId}/menu/menu_row/${rowId}`
  );
};
export const useDeleteMenuRow = (storeId: string, rowId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => deleteMenuRow(storeId, rowId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'active',
      });
      queryClient.resetQueries({
        queryKey: ['menu-paginated', storeId],
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'active',
      });
    },
  });
};

interface UpdateAppearanceParams {
  pdp_hide_description: boolean;
  pdp_hide_effects: boolean;
  view_default: 'list_view' | 'grid_view';
  white_label_nav_color: string;
  white_label_theme_color: string;
}

const updateAppearance = async (
  storeId: string,
  data: UpdateAppearanceParams
) => {
  return await janeApiV2.patch<null>(
    `${STORES_URL}/${storeId}/menu/appearance`,
    {
      appearance: data,
    }
  );
};

export const useUpdateAppearance = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAppearanceParams) =>
      updateAppearance(storeId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

export interface UpdateCommsBannerParams {
  alt_text?: string;
  banner_type: string;
  button_fill_color?: string;
  button_link?: string;
  button_text?: string;
  button_text_color?: string;
  enabled: boolean;
  header_text_color?: string;
  image?: string;
  image_opacity?: number;
  is_expanded: boolean;
  message: string;
  stores?: number[] | string[];
  title: string;
}

const updateCommsBanner = async (
  storeId: string,
  data: UpdateCommsBannerParams
) => {
  return await janeApiV2.patch<null>(
    `${STORES_URL}/${storeId}/menu/communication_banner`,
    {
      communication_banner: data,
    }
  );
};

export const useUpdateCommsBanner = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCommsBannerParams) =>
      updateCommsBanner(storeId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

export type UpdateMenuRowOrderBody = {
  enabled?: boolean;
  id?: number;
  product_type: string;
  ranking: number;
}[];

const updateMenuRowOrder = async (
  data: UpdateMenuRowOrderBody,
  storeId?: string
): Promise<null> => {
  return await janeApiV2.put<null>(`${STORES_URL}/${storeId}/menu/menu_rows`, {
    menu_rankings: data,
  });
};

export const useUpdateMenuRowOrder = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateMenuRowOrderBody) =>
      updateMenuRowOrder(data, storeId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

const updateFiltersAndLabels = async (
  data: FiltersAndLabels,
  storeId?: string
): Promise<null> => {
  return await janeApiV2.patch<null>(
    `${STORES_URL}/${storeId}/menu/filters_and_labels`,
    {
      filters_and_labels: data,
    }
  );
};

export const useUpdateFiltersAndLabels = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FiltersAndLabels) =>
      updateFiltersAndLabels(data, storeId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

export interface UpdateCarouselBannerParams {
  carousel_banners?: Omit<CarouselBanner, 'store_id' | 'image_url'>[];
  deleted_carousel_banners?: number[];
  stores?: Array<number>;
}

const updateCarouselBanner = async (
  storeId: string,
  data: UpdateCarouselBannerParams
) => {
  return await janeApiV2.patch<null>(
    `${STORES_URL}/${storeId}/menu/carousel_banner`,
    {
      carousel_banner: data,
    }
  );
};

export const useUpdateCarouselBanner = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateCarouselBannerParams) =>
      updateCarouselBanner(storeId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

export type MenuResponse = {
  appearance: Appearance;
  carousel_banner: CarouselBanner[];
  communication_banner: CommunicationBanner;
  filters_and_labels: FiltersAndLabels;
  magic_row_count?: number;
  menu_rows: MenuRow[];
  menu_tabs: MenuTab[];
};

export type MenuResponsePaginated = MenuResponse & {
  pagination: {
    has_next_page: boolean;
    has_prev_page: boolean;
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
};

const fetchMenu = async (storeId?: string): Promise<MenuResponse> => {
  const url = `${STORES_URL}/${storeId}/menu`;
  const tPayload = t.type({
    menu_rows: t.array(tMenuRow),
  });
  const data = await fetchWithDecode(
    janeApiV2.get<MenuResponse>(url),
    tPayload,
    url
  );

  return data;
};

export const useStoreMenu = (storeId?: string) => {
  return useQuery<MenuResponse>({
    queryFn: () => fetchMenu(storeId),
    queryKey: ['menu', storeId],
    staleTime: Infinity,
    useErrorBoundary: false,
  });
};

// Paginated menu fetch for infinite scroll
// TODO: swap to safeParseAsync when possible & make zod types
const fetchMenuPaginated = async (
  storeId?: string,
  page = 1,
  perPage = 20
): Promise<MenuResponsePaginated> => {
  const url = `${STORES_URL}/${storeId}/menu?page=${page}&per_page=${perPage}`;
  const tPayload = t.type({
    menu_rows: t.array(tMenuRow),
    pagination: t.type({
      has_next_page: t.boolean,
      has_prev_page: t.boolean,
      page: t.number,
      per_page: t.number,
      total: t.number,
      total_pages: t.number,
    }),
  });
  const data = await fetchWithDecode(
    janeApiV2.get<MenuResponsePaginated>(url),
    tPayload,
    url
  );

  return data;
};

export const useStoreMenuPaginated = (storeId?: string, perPage = 20) => {
  return useInfiniteQuery<MenuResponsePaginated>({
    queryKey: ['menu-paginated', storeId, perPage],
    queryFn: ({ pageParam = 1 }) =>
      fetchMenuPaginated(storeId, pageParam as number, perPage),
    getNextPageParam: (lastPage) => {
      if (lastPage.pagination.has_next_page) {
        return lastPage.pagination.page + 1;
      }
      return undefined;
    },
    enabled: !!storeId,
    staleTime: Infinity,
    useErrorBoundary: false,
  });
};

// Fast metadata-only fetch for modals (skips expensive menu_rows computation)
const fetchMenuMetadata = async (storeId?: string): Promise<MenuResponse> => {
  const url = `${STORES_URL}/${storeId}/menu?metadata_only=true`;
  const tPayload = t.type({
    menu_rows: t.array(tMenuRow),
  });
  const data = await fetchWithDecode(
    janeApiV2.get<MenuResponse>(url),
    tPayload,
    url
  );

  return data;
};

export const useStoreMenuMetadata = (storeId?: string) => {
  return useQuery<MenuResponse>({
    queryFn: () => fetchMenuMetadata(storeId),
    queryKey: ['menu-metadata', storeId],
    staleTime: Infinity,
    useErrorBoundary: false,
    enabled: !!storeId,
  });
};

interface UpsertEmbeddedMenuRowParams {
  colors: EmbeddedMenuRow['colors'];
  fonts: EmbeddedMenuRow['fonts'];
  header?: string;
}

const upsertEmbeddedMenuRow = async (
  storeId: string,
  rowId: number,
  data: UpsertEmbeddedMenuRowParams
): Promise<{ embedded_menu_row: EmbeddedMenuRow }> => {
  return await janeApiV2.put<{ embedded_menu_row: EmbeddedMenuRow }>(
    `${STORES_URL}/${storeId}/menu/menu_row/${rowId}/embedded`,
    { embedded_menu_row: data }
  );
};

export const useUpsertEmbeddedMenuRow = (storeId: string, rowId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertEmbeddedMenuRowParams) =>
      upsertEmbeddedMenuRow(storeId, rowId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};

export type UpdateMenuTabOrderBody = MenuTab[];

const updateMenuTabOrder = async (
  data: UpdateMenuTabOrderBody,
  storeId?: string
): Promise<null> => {
  return await janeApiV2.put<null>(`${STORES_URL}/${storeId}/menu/menu_tabs`, {
    menu_tabs: data,
  });
};

export const useUpdateMenuTabOrder = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateMenuTabOrderBody) =>
      updateMenuTabOrder(data, storeId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['menu', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-paginated', storeId],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['menu-metadata', storeId],
        refetchType: 'all',
      });
    },
  });
};
